import React, {useEffect, useRef, useState, useId} from 'react';
import {t} from 'i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from '@mui/material';
import Select, {SelectChangeEvent} from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import {indexingDataType} from '../../model';
import {useAnalytics} from '../../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../../constants/analyticsConstants';

interface BuildType {
  analyzer: string;
  name: string;
  label?: string;
  isVisible?: boolean;
}

interface FieldData {
  fieldLabel: string;
  dataType: string;
  analyzer: string;
  label: string;
}

interface ConfigureIndexTableProps {
  buildTypeList: BuildType[];
  tableData: FieldData[];
  handleChangeAnalyzers: (data: any) => void;
}
const ConfigureIndexTable: React.FC<ConfigureIndexTableProps> = ({
  buildTypeList,
  tableData,
  handleChangeAnalyzers,
}) => {
  const [selectedAnalyzers, setSelectedAnalyzers] = useState(
    tableData.reduce(
      (acc, row) => {
        acc[row.fieldLabel] = row.analyzer;
        return acc;
      },
      {} as Record<string, string>
    )
  );
  const dropdownRefs = useRef<(HTMLDivElement | null)[]>([]);
  const {trackGAEvent} = useAnalytics();
  const changeAnalyzerHintID = useId();
  const groupData = () => {
    return tableData.reduce(
      (acc, row) => {
        const analyzer = selectedAnalyzers[row.fieldLabel] || row.analyzer;
        if (!acc[analyzer]) {
          acc[analyzer] = [];
        }
        acc[analyzer].push(row);
        return acc;
      },
      {} as Record<string, typeof tableData>
    );
  };

  const handleChange = (
    event: SelectChangeEvent,
    name: string,
    index: number
  ) => {
    const selectedItem = buildTypeList.find(
      (item) => item.name === event.target.value
    );
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: selectedItem?.label ?? '',
        type: GA_CLICK_TYPES.DROPDOWN,
        region: GA_REGIONS.TAB,
        action: t('gaTracking.indexManagement.newIndexAnalyzerSelected'),
      },
      subPage: {
        subpage_name: t('gaTracking.indexManagement.configureIndex'),
        subpage_type: GA_SUBPAGE_TYPE.TAB,
      },
    });

    const newSelectedAnalyzers = {
      ...selectedAnalyzers,
      [name]: event.target.value,
    };
    setSelectedAnalyzers(newSelectedAnalyzers);
    setTimeout(() => {
      dropdownRefs.current[index]?.focus();
    }, 100);
  };

  const colorCodes = [
    '#00AFB8',
    '#4656DA',
    '#D9E277',
    '#8AA5FF',
    '#FFD6A5',
    '#D6C4F1',
    '#A5E9D0',
    '#FF9E2C',
    '#B290E5',
    '#FFCCCC',
    '#55D5A7',
    '#764CB6',
    '#C9043F',
    '#04A269',
    '#B8BBC0',
  ];

  const groupedData = groupData();

  useEffect(() => {
    handleChangeAnalyzers(selectedAnalyzers);
  }, [selectedAnalyzers]);

  return (
    <TableContainer
      component={Paper}
      className="data-table configure-index-table configure-index-container "
    >
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <div>
                <Typography className="typo-text label2_size index-table-text">
                  {t(`indexes.configureIndex.configureIndexheader1`)}
                </Typography>
              </div>
            </TableCell>
            <TableCell>
              <div>
                <div className="column-separator"></div>
                <Typography className="typo-text label2_size index-table-text">
                  {t(`indexes.configureIndex.configureIndexheader2`)}
                </Typography>
              </div>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.keys(groupedData).map((analyzerName, index) => {
            const getGroupDataByAnalyzerName = groupedData[analyzerName];
            const getAnalyzerLabel = (analyzerName: string) =>
              buildTypeList.find((item) => item.name === analyzerName)?.label ??
              analyzerName;
            return (
              <React.Fragment key={analyzerName}>
                <TableRow>
                  <TableCell colSpan={2}>
                    <div
                      style={{
                        padding: '0px',
                        marginTop: index === 0 ? '0px' : '10px',
                      }}
                    ></div>{' '}
                    {/* Only apply margin for non-first group */}
                  </TableCell>
                </TableRow>

                {[...getGroupDataByAnalyzerName]
                  .sort((a, b) => a.fieldLabel.localeCompare(b.fieldLabel))
                  .map((row) => (
                    <TableRow
                      key={row.fieldLabel}
                      tabIndex={0}
                      aria-label={t(
                        'indexes.configureIndex.analyzerFullLabel',
                        {
                          fieldLabel: `${row.fieldLabel}`,
                          analyzerLabel: `${getAnalyzerLabel(selectedAnalyzers[row.fieldLabel])}`,
                        }
                      )}
                    >
                      <TableCell className="index-tabel-cell">
                        <div className="table-type-cell index-table-text">
                          <Typography className="typo-text body3_size custom-row-text">
                            {row.fieldLabel}
                          </Typography>
                        </div>
                      </TableCell>
                      <TableCell align="left" className="index-tabel-cell">
                        <Select
                          inputProps={{
                            'aria-label': `${row.fieldLabel}`,
                          }}
                          className="typo-text body3_size table-width"
                          value={selectedAnalyzers[row.fieldLabel]}
                          inputRef={(el) => (dropdownRefs.current[index] = el)}
                          onChange={(e) =>
                            handleChange(e, row.fieldLabel, index)
                          }
                          onMouseDown={(e) => {
                            if (row.dataType !== indexingDataType.text) {
                              e.preventDefault();
                            }
                          }}
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key == 'Enter' || e.key == ' ') {
                              if (row.dataType !== indexingDataType.text) {
                                e.preventDefault();
                                e.stopPropagation();
                              }
                            }
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          aria-describedby={changeAnalyzerHintID}
                          MenuProps={{
                            PaperProps: {
                              role:"main",
                              style: {
                                minWidth: 'unset',
                                maxWidth: 'unset',
                                width: '359px',
                                left: '520px',
                                transform: 'translateX(2%)',
                                maxHeight: '572px',
                                overflowY: 'auto',
                              },
                              sx: {
                                '.MuiMenuItem-root.Mui-selected': {
                                  color: '#fff', // Optional: make text color white to contrast
                                },
                              },
                            },
                          }}
                          sx={{
                            boxShadow: 'none',
                            pointerEvents:
                              row.dataType !== indexingDataType.text
                                ? 'none'
                                : 'auto',
                            '.MuiOutlinedInput-notchedOutline': {border: 0},
                            '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
                              {
                                border: 0,
                              },
                            '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                              {
                                border: 0,
                              },
                            '& .MuiSelect-icon': {
                              color: '#89A6FF', // Change icon color
                              display:
                                row.dataType === indexingDataType.text
                                  ? 'block'
                                  : 'none',
                            },
                          }}
                        >
                          {buildTypeList.map((item, idx) =>
                            analyzerName === item.name || item.isVisible ? (
                              <MenuItem
                                key={`${item.name}_${idx}`}
                                value={item.name}
                                sx={{
                                  marginBottom: '12px',
                                  '&:hover': {
                                    backgroundColor: 'transparent',
                                  },
                                }}
                              >
                                <span
                                  className="typo-text body3_size"
                                  style={{
                                    padding: '3px 5px',
                                    borderRadius: '3px',
                                    backgroundColor:
                                      colorCodes[idx % colorCodes.length] ||
                                      'transparent',
                                    color: [
                                      '#4656DA',
                                      '#764CB6',
                                      '#C9043F',
                                      '#04A269',
                                    ].includes(
                                      colorCodes[idx % colorCodes.length]
                                    )
                                      ? '#FFFFFF'
                                      : '#15181C',
                                  }}
                                >
                                  {item.label}
                                </span>
                              </MenuItem>
                            ) : (
                              <></>
                            )
                          )}
                        </Select>
                        <span id={changeAnalyzerHintID} className="sr-only">
                          {t(
                            'indexes.customIndex.step3.changeAnalyzerHint'
                          )}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ConfigureIndexTable;
